/**
 * Centralized Axios Instance
 * Configured with base URL, credentials, and interceptors for token management
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { notificationService } from '../services/notificationService';
import { getBrowserTimeZone, TIMEZONE_HEADER } from './timezone';

// ============================================
// Axios Instance Configuration
// ============================================

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_SERVER_URL,
  withCredentials: true, // Required for HttpOnly cookies (refresh token)
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// In-Memory Token Storage
// ============================================

let accessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = (): string | null => {
  return accessToken;
};

export const clearAccessToken = () => {
  accessToken = null;
};

let refreshPromise: Promise<string> | null = null;

const AUTH_REFRESH_EXCLUDED_PATHS = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/auth/anonymous',
  '/auth/forgot-password',
  '/auth/reset-password',
]);

const shouldAttemptRefresh = (url?: string): boolean => {
  if (!url) {
    return true;
  }

  const baseUrl =
    import.meta.env.VITE_SERVER_URL ||
    (typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  const pathname = url.startsWith('http') ? new URL(url).pathname : new URL(url, baseUrl).pathname;

  return !AUTH_REFRESH_EXCLUDED_PATHS.has(pathname);
};

/**
 * True when the server rejected the refresh session itself (401/403), as opposed to
 * a transient failure (5xx, network). Only a rejection should log the admin out.
 */
export const isRefreshSessionRejected = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return true;
  }

  const status = error.response?.status;
  return status === 401 || status === 403;
};

export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = axios
    .post(
      `${import.meta.env.VITE_SERVER_URL}/auth/refresh`,
      {},
      {
        withCredentials: true,
      }
    )
    .then((response) => {
      console.log('[Axios] Refresh response:', response.data);

      const newAccessToken = response.data.data?.accessToken;
      if (!newAccessToken) {
        throw new Error('No access token in refresh response');
      }

      setAccessToken(newAccessToken);
      return newAccessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

// ============================================
// Request Interceptor
// Automatically attach access token to requests
// ============================================

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.headers) {
      const timeZone = getBrowserTimeZone();
      if (timeZone) config.headers[TIMEZONE_HEADER] = timeZone;
    }

    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`[Axios] Request to ${config.url} with token`);
    } else {
      console.log(`[Axios] Request to ${config.url} without token`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ============================================
// Response Interceptor
// Handle 401 errors and token refresh
// ============================================

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ message?: string; error?: string }>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't retried yet
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      shouldAttemptRefresh(originalRequest.url)
    ) {
      originalRequest._retry = true;

      try {
        console.log('[Axios] Attempting token refresh...');
        const newAccessToken = await refreshAccessToken();
        console.log('[Axios] New access token set, retrying queued requests');

        // Retry the original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('[Axios] Token refresh failed:', refreshError);
        clearAccessToken();
        // Only a rejected session logs the admin out; transient server errors keep it.
        if (!isRefreshSessionRejected(refreshError)) {
          notificationService.error('Server is temporarily unavailable. Please try again.');
          return Promise.reject(refreshError);
        }
        notificationService.error('Session expired. Please log in again.');
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      }
    }

    // Show error toast for 4xx and 5xx errors (except 401 which is handled above)
    if (error.response) {
      const status = error.response.status;

      // Skip showing toast for 401 (handled by token refresh logic above)
      if (status !== 401) {
        const errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          `Request failed with status ${status}`;

        notificationService.error(errorMessage);
      }
    } else if (error.request) {
      // Network error (no response received)
      notificationService.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
